import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { Button } from './ui/Button';
interface PrePaymentFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
  ticketName: string;
  ticketPrice: string;
  ticketType?: 'festival-only' | 'accommodation' | 'double-alone';
}
interface FormData {
  fullName: string;
  phone: string;
  email: string;
  gender: 'male' | 'female' | '';
  roomType: 'double' | 'triple' | '';
  sharedRoomAcknowledgment: boolean;
  roommate1Name: string;
  roommate2Name: string;
  competitiveTrack: string[];
  rowingType: 'sup' | 'race' | 'surfski' | 'surfer' | 'atmosphere' | '';
  belongsToClub: 'yes' | 'no' | '';
  clubName: string;
  rentBoard: 'yes' | 'no' | '';
  healthDeclaration: boolean;
  medicalCheckups: boolean;
  riskAwareness: boolean;
  liabilityWaiver: boolean;
  medicalTreatment: boolean;
  photographyConsent: boolean;
  cancellationPolicy: boolean;
  whatsappGroup: boolean;
}
export function WaterTribeFestPrePaymentForm({
  isOpen,
  onClose,
  onSubmit,
  ticketName,
  ticketPrice,
  ticketType = 'accommodation'
}: PrePaymentFormProps) {
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    phone: '',
    email: '',
    gender: '',
    roomType: '',
    sharedRoomAcknowledgment: false,
    roommate1Name: '',
    roommate2Name: '',
    competitiveTrack: [],
    rowingType: '',
    belongsToClub: '',
    clubName: '',
    rentBoard: '',
    healthDeclaration: false,
    medicalCheckups: false,
    riskAwareness: false,
    liabilityWaiver: false,
    medicalTreatment: false,
    photographyConsent: false,
    cancellationPolicy: false,
    whatsappGroup: false
  });
  const isFormValid = () => {
    const basicFieldsValid = formData.fullName.trim() !== '' && formData.phone.trim() !== '' && formData.email.trim() !== '' && formData.gender !== '' && formData.rentBoard !== '';
    // Room fields only required for accommodation tickets (not for festival-only or double-alone)
    const roomFieldsValid = ticketType === 'accommodation' ? formData.roomType !== '' && formData.sharedRoomAcknowledgment && formData.roommate1Name.trim() !== '' : true;
    const declarationsValid = formData.healthDeclaration && formData.medicalCheckups && formData.riskAwareness && formData.liabilityWaiver && formData.medicalTreatment && formData.photographyConsent && formData.cancellationPolicy;
    return basicFieldsValid && roomFieldsValid && declarationsValid;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid()) {
      onSubmit(formData);
    }
  };
  const handleCheckboxChange = (field: keyof FormData) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  return <AnimatePresence data-id="element-281">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80]" onClick={onClose} data-id="element-282" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-4 md:inset-8 lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:max-w-4xl lg:max-h-[85vh] lg:w-full bg-white rounded-3xl shadow-2xl z-[90] overflow-hidden flex flex-col" dir="rtl" data-id="element-283">
            <div className="flex-1 overflow-y-auto" data-id="element-284">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-br from-indigo-600 to-purple-600 text-white p-6 md:p-8 z-10 shadow-lg" data-id="element-285">
                <button onClick={onClose} className="absolute top-4 left-4 md:top-6 md:left-6 bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors" data-id="element-286">
                  <X className="w-5 h-5 md:w-6 md:h-6" data-id="element-287" />
                </button>
                <h2 className="text-2xl md:text-3xl font-bold mb-3" data-id="element-288">
                  טופס הרשמה לפסטיבל
                </h2>
                <div className="flex items-center justify-between text-sm md:text-base bg-white/20 backdrop-blur-sm rounded-xl p-4" data-id="element-289">
                  <span className="font-medium" data-id="element-290">{ticketName}</span>
                  <span className="font-bold text-lg" data-id="element-291">₪{ticketPrice}</span>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-8" data-id="element-292">
                {/* Personal Information */}
                <div className="space-y-4" data-id="element-293">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-294">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-295">
                      <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-296">
                        1
                      </span>
                    </div>
                    פרטים אישיים
                  </h3>

                  <div className="space-y-4" data-id="element-297">
                    <div data-id="element-298">
                      <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-299">
                        שם מלא <span className="text-red-500" data-id="element-300">*</span>
                      </label>
                      <input type="text" required value={formData.fullName} onChange={e => handleInputChange('fullName', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="הזן שם מלא" data-id="element-301" />
                    </div>

                    <div data-id="element-302">
                      <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-303">
                        טלפון <span className="text-red-500" data-id="element-304">*</span>
                      </label>
                      <input type="tel" required value={formData.phone} onChange={e => handleInputChange('phone', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="050-1234567" data-id="element-305" />
                    </div>

                    <div data-id="element-306">
                      <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-307">
                        אימייל <span className="text-red-500" data-id="element-308">*</span>
                      </label>
                      <input type="email" required value={formData.email} onChange={e => handleInputChange('email', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="example@email.com" data-id="element-309" />
                    </div>
                  </div>
                </div>

                {/* Event Details - Accommodation Section (conditional) */}
                {ticketType === 'accommodation' && <div className="space-y-4" data-id="element-310">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-311">
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-312">
                        <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-313">
                          2
                        </span>
                      </div>
                      פרטי לינה
                    </h3>

                    <div className="space-y-4" data-id="element-314">
                      {/* Room Type */}
                      <div data-id="element-315">
                        <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-316">
                          סוג החדר <span className="text-red-500" data-id="element-317">*</span>
                        </label>
                        <div className="space-y-2" data-id="element-318">
                          <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-319">
                            <input type="radio" name="roomType" value="double" checked={formData.roomType === 'double'} onChange={e => setFormData({
                        ...formData,
                        roomType: 'double'
                      })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-320" />
                            <span className="text-sm md:text-base" data-id="element-321">זוגי</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-322">
                            <input type="radio" name="roomType" value="triple" checked={formData.roomType === 'triple'} onChange={e => setFormData({
                        ...formData,
                        roomType: 'triple'
                      })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-323" />
                            <span className="text-sm md:text-base" data-id="element-324">
                              חדר פרמיום גדול- טריפל (עד 3 מבוגרים בחדר)
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* Shared Room Acknowledgment */}
                      <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-amber-50 rounded-xl cursor-pointer hover:bg-amber-100 transition-colors border-2 border-amber-200" data-id="element-325">
                        <input type="checkbox" checked={formData.sharedRoomAcknowledgment} onChange={() => handleCheckboxChange('sharedRoomAcknowledgment')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-326" />
                        <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-327">
                          <strong data-id="element-328">
                            ידוע לי כי הזמנה זו היא עבור לינה בחדר משותף
                          </strong>{' '}
                          - אני מאשר/ת כי מצאתי שותף/ה לחדר מראש, ובאחריותי
                          לוודא שגם השותף/ה מבצע/ת הרשמה והסדרה של התשלום באופן
                          עצמאי <span className="text-red-500" data-id="element-329">*</span>
                        </span>
                      </label>

                      {/* Roommate 1 */}
                      <div data-id="element-330">
                        <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-331">
                          שם שותף/ה 1 לחדר{' '}
                          <span className="text-red-500" data-id="element-332">*</span>
                        </label>
                        <input type="text" required={ticketType === 'accommodation'} value={formData.roommate1Name} onChange={e => handleInputChange('roommate1Name', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="שם מלא של שותף/ה" data-id="element-333" />
                      </div>

                      {/* Roommate 2 (conditional) */}
                      {formData.roomType === 'triple' && <div data-id="element-334">
                          <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-335">
                            רק בחדר טריפל - שם שותף/ה 2 לחדר (אופציונלי)
                          </label>
                          <input type="text" value={formData.roommate2Name} onChange={e => handleInputChange('roommate2Name', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="שם מלא של שותף/ה שני (לא חובה)" data-id="element-336" />
                        </div>}
                    </div>
                  </div>}

                {/* Event Details - Activity Section */}
                <div className="space-y-4" data-id="element-337">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-338">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-339">
                      <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-340">
                        {ticketType === 'accommodation' ? '3' : ticketType === 'double-alone' ? '2' : '2'}
                      </span>
                    </div>
                    פרטי השתתפות
                  </h3>

                  <div className="space-y-4" data-id="element-341">
                    {/* Gender */}
                    <div data-id="element-342">
                      <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-343">
                        מין <span className="text-red-500" data-id="element-344">*</span>
                      </label>
                      <div className="flex gap-4" data-id="element-345">
                        <label className="flex items-center gap-2 cursor-pointer" data-id="element-346">
                          <input type="radio" name="gender" value="male" checked={formData.gender === 'male'} onChange={e => setFormData({
                        ...formData,
                        gender: 'male'
                      })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-347" />
                          <span className="text-sm md:text-base" data-id="element-348">זכר</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer" data-id="element-349">
                          <input type="radio" name="gender" value="female" checked={formData.gender === 'female'} onChange={e => setFormData({
                        ...formData,
                        gender: 'female'
                      })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-350" />
                          <span className="text-sm md:text-base" data-id="element-351">נקבה</span>
                        </label>
                      </div>
                    </div>

                    {/* Competitive Track */}
                    <div data-id="element-352">
                      <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-353">
                        מעוניין להשתתף במסלול הישגי? (ניתן לבחור יותר ממסלול
                        אחד)
                      </label>
                      <div className="space-y-2" data-id="element-354">
                        {[{
                      value: 'no',
                      label: 'לא'
                    }, {
                      value: 'sprint',
                      label: 'מקצה ספרינט'
                    }, {
                      value: 'technical',
                      label: 'מקצה טכני'
                    }, {
                      value: 'longDistance',
                      label: 'מקצה לונג דיסטנס'
                    }].map(option => <label key={option.value} className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-355">
                            <input type="checkbox" name="competitiveTrack" value={option.value} checked={formData.competitiveTrack.includes(option.value)} onChange={e => {
                        const value = option.value;
                        setFormData(prev => ({
                          ...prev,
                          competitiveTrack: e.target.checked ? [...prev.competitiveTrack, value] : prev.competitiveTrack.filter(v => v !== value)
                        }));
                      }} className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500" data-id="element-356" />
                            <span className="text-sm md:text-base" data-id="element-357">
                              {option.label}
                            </span>
                          </label>)}
                      </div>
                    </div>

                    {/* Rowing Type */}
                    <div data-id="element-358">
                      <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-359">
                        חותר ב-
                      </label>
                      <div className="space-y-2" data-id="element-360">
                        {[{
                      value: 'sup',
                      label: 'סאפ'
                    }, {
                      value: 'race',
                      label: 'רייס'
                    }, {
                      value: 'surfski',
                      label: 'סרפסקי'
                    }, {
                      value: 'surfer',
                      label: 'גולש'
                    }, {
                      value: 'atmosphere',
                      label: 'סופג אווירה'
                    }].map(option => <label key={option.value} className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-361">
                            <input type="radio" name="rowingType" value={option.value} checked={formData.rowingType === option.value} onChange={e => setFormData({
                        ...formData,
                        rowingType: option.value as any
                      })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-362" />
                            <span className="text-sm md:text-base" data-id="element-363">
                              {option.label}
                            </span>
                          </label>)}
                      </div>
                    </div>

                    {/* Rowing Club */}
                    <div data-id="element-364">
                      <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-365">
                        שייך למועדון חתירה?
                      </label>
                      <div className="flex gap-4 mb-3" data-id="element-366">
                        <label className="flex items-center gap-2 cursor-pointer" data-id="element-367">
                          <input type="radio" name="belongsToClub" value="yes" checked={formData.belongsToClub === 'yes'} onChange={e => setFormData({
                        ...formData,
                        belongsToClub: 'yes'
                      })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-368" />
                          <span className="text-sm md:text-base" data-id="element-369">כן</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer" data-id="element-370">
                          <input type="radio" name="belongsToClub" value="no" checked={formData.belongsToClub === 'no'} onChange={e => setFormData({
                        ...formData,
                        belongsToClub: 'no',
                        clubName: ''
                      })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-371" />
                          <span className="text-sm md:text-base" data-id="element-372">לא</span>
                        </label>
                      </div>

                      {formData.belongsToClub === 'yes' && <div data-id="element-373">
                          <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-374">
                            במידה וכן, לאיזה מועדון?
                          </label>
                          <input type="text" value={formData.clubName} onChange={e => handleInputChange('clubName', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="שם המועדון" data-id="element-375" />
                        </div>}
                    </div>

                    {/* Rent Board */}
                    <div data-id="element-376">
                      <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-377">
                        מעוניין להשכיר גלשן לאירוע?{' '}
                        <span className="text-red-500" data-id="element-378">*</span>
                      </label>
                      <div className="flex gap-4" data-id="element-379">
                        <label className="flex items-center gap-2 cursor-pointer" data-id="element-380">
                          <input type="radio" name="rentBoard" value="yes" checked={formData.rentBoard === 'yes'} onChange={e => setFormData({
                        ...formData,
                        rentBoard: 'yes'
                      })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-381" />
                          <span className="text-sm md:text-base" data-id="element-382">כן</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer" data-id="element-383">
                          <input type="radio" name="rentBoard" value="no" checked={formData.rentBoard === 'no'} onChange={e => setFormData({
                        ...formData,
                        rentBoard: 'no'
                      })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-384" />
                          <span className="text-sm md:text-base" data-id="element-385">לא</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Health & Safety Declarations */}
                <div className="space-y-4" data-id="element-386">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-387">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-388">
                      <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-389">
                        {ticketType === 'accommodation' ? '4' : ticketType === 'double-alone' ? '3' : '3'}
                      </span>
                    </div>
                    הצהרות בריאות ובטיחות
                  </h3>

                  <div className="space-y-3" data-id="element-390">
                    <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-391">
                      <input type="checkbox" checked={formData.healthDeclaration} onChange={() => handleCheckboxChange('healthDeclaration')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-392" />
                      <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-393">
                        אני מצהיר/ה כי אני בריא/ה, בכושר גופני מתאים, וכי אין לי
                        כל מגבלה רפואית או גופנית המונעת ממני להשתתף בפעילות
                        הספורט ו/או במקצים השונים באירוע.
                      </span>
                    </label>

                    <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-394">
                      <input type="checkbox" checked={formData.medicalCheckups} onChange={() => handleCheckboxChange('medicalCheckups')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-395" />
                      <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-396">
                        ידוע לי כי על פי חוק הספורט, האחריות לביצוע בדיקות
                        רפואיות תקופתיות חלה עליי, ואני מאשר/ת כי עשיתי כן וכי
                        נמצאתי כשיר/ה לפעילות המתוכננת.
                      </span>
                    </label>

                    <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-397">
                      <input type="checkbox" checked={formData.riskAwareness} onChange={() => handleCheckboxChange('riskAwareness')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-398" />
                      <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-399">
                        אני מודע/ת לכך שהשתתפות בפעילות ספורטיבית כרוכה במאמץ
                        גופני ובסיכונים פציעה אינהרנטיים. אני בוחר/ת להשתתף
                        בפעילות מרצוני החופשי ולאחר ששקלתי את הסיכונים.
                      </span>
                    </label>
                  </div>
                </div>

                {/* Liability Waiver */}
                <div className="space-y-4" data-id="element-400">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-401">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-402">
                      <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-403">
                        {ticketType === 'accommodation' ? '5' : ticketType === 'double-alone' ? '4' : '4'}
                      </span>
                    </div>
                    שחרור מאחריות
                  </h3>

                  <div className="space-y-3" data-id="element-404">
                    <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-405">
                      <input type="checkbox" checked={formData.liabilityWaiver} onChange={() => handleCheckboxChange('liabilityWaiver')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-406" />
                      <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-407">
                        אני מוותר/ת בזאת באופן מלא ומוחלט על כל טענה, דרישה או
                        תביעה כנגד מארגני האירוע, המדריכים, נותני החסות או בעלי
                        המקום, בגין כל נזק גוף, נזק רכוש או אובדן שייגרם לי
                        במהלך האירוע או כתוצאה ממנו, למעט במקרים של רשלנות פושעת
                        מצד המארגנים.
                      </span>
                    </label>

                    <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-408">
                      <input type="checkbox" checked={formData.medicalTreatment} onChange={() => handleCheckboxChange('medicalTreatment')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-409" />
                      <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-410">
                        אני מאשר/ת למארגנים להעניק לי עזרה ראשונה או לפנות אותי
                        לטיפול רפואי במידת הצורך, ומסכים/ה לשאת בעלויות הכרוכות
                        בכך (כגון אמבולנס) ככל שיידרש.
                      </span>
                    </label>
                  </div>
                </div>

                {/* Photography & Media */}
                <div className="space-y-4" data-id="element-411">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-412">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-413">
                      <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-414">
                        {ticketType === 'accommodation' ? '6' : ticketType === 'double-alone' ? '5' : '5'}
                      </span>
                    </div>
                    צילום ופרסום
                  </h3>

                  <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-415">
                    <input type="checkbox" checked={formData.photographyConsent} onChange={() => handleCheckboxChange('photographyConsent')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-416" />
                    <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-417">
                      אני נותן/ת בזאת את הסכמתי למארגני האירוע או למי מטעמם,
                      לצלם אותי במהלך הפעילות. אני מסכים/ה כי המארגנים יוכלו
                      לעשות שימוש בתמונות ובסרטונים אלו לצרכי תיעוד, פרסום,
                      שיווק ויחסי ציבור של האירוע, בכל מדיה, ללא הגבלת זמן וללא
                      תמורה כספית.
                    </span>
                  </label>
                </div>

                {/* Cancellation Policy */}
                <div className="space-y-4" data-id="element-418">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-419">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-420">
                      <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-421">
                        {ticketType === 'accommodation' ? '7' : ticketType === 'double-alone' ? '6' : '6'}
                      </span>
                    </div>
                    מדיניות ביטולים
                  </h3>

                  <div className="border-2 border-gray-200 rounded-xl overflow-hidden" data-id="element-422">
                    <button type="button" onClick={() => setShowCancellationPolicy(!showCancellationPolicy)} className="w-full flex items-center justify-between p-4 md:p-5 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-423">
                      <span className="font-medium text-gray-900 text-sm md:text-base" data-id="element-424">
                        לחץ לקריאת מדיניות הביטולים
                      </span>
                      {showCancellationPolicy ? <ChevronUp className="w-5 h-5 md:w-6 md:h-6 text-gray-600 shrink-0" data-id="element-425" /> : <ChevronDown className="w-5 h-5 md:w-6 md:h-6 text-gray-600 shrink-0" data-id="element-426" />}
                    </button>

                    {showCancellationPolicy && <div className="p-4 md:p-5 bg-white space-y-3 text-sm md:text-base text-gray-700 border-t-2 border-gray-200" data-id="element-427">
                        <p className="font-medium" data-id="element-428">תנאי ביטול:</p>
                        <ul className="list-disc list-inside space-y-2 mr-4" data-id="element-429">
                          <li data-id="element-430">
                            ביטול בין שלושה שבועות לשבועיים לפני מועד האירוע –
                            יחויבו 50% מעלות ההשתתפות
                          </li>
                          <li data-id="element-431">
                            ביטול עד שבוע לפני מועד האירוע – יחויבו 70% מעלות
                            ההשתתפות
                          </li>
                          <li data-id="element-432">
                            ביטול במהלך השבוע שלפני מועד האירוע (7 ימים ומטה) –
                            יחויבו 100% מעלות ההשתתפות
                          </li>
                        </ul>
                      </div>}
                  </div>

                  <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-433">
                    <input type="checkbox" checked={formData.cancellationPolicy} onChange={() => handleCheckboxChange('cancellationPolicy')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-434" />
                    <span className="text-sm md:text-base text-gray-700 leading-relaxed font-medium" data-id="element-435">
                      קראתי והבנתי את מדיניות הביטולים
                    </span>
                  </label>
                </div>

                {/* WhatsApp Group (Optional) */}
                <div className="space-y-4" data-id="element-436">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-green-100" data-id="element-437">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0" data-id="element-438">
                      <span className="text-green-600 font-bold text-sm md:text-base" data-id="element-439">
                        {ticketType === 'accommodation' ? '8' : ticketType === 'double-alone' ? '7' : '7'}
                      </span>
                    </div>
                    קבוצת וואטסאפ (אופציונלי)
                  </h3>

                  <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-green-50 rounded-xl cursor-pointer hover:bg-green-100 transition-colors border-2 border-transparent hover:border-green-200" data-id="element-440">
                    <input type="checkbox" checked={formData.whatsappGroup} onChange={() => handleCheckboxChange('whatsappGroup')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-green-600 rounded focus:ring-green-500 shrink-0" data-id="element-441" />
                    <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-442">
                      אני מעוניין/ת שתוסיפו אותי לקבוצת הוואטסאפ השקטה של האירוע
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <div className="sticky bottom-0 bg-white pt-6 pb-2 border-t-2 border-gray-200 -mx-6 md:-mx-8 px-6 md:px-8" data-id="element-443">
                  {!isFormValid() && <div className="flex items-start gap-3 mb-4 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl" data-id="element-444">
                      <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-amber-600 shrink-0 mt-0.5" data-id="element-445" />
                      <p className="text-sm md:text-base text-amber-800 leading-relaxed" data-id="element-446">
                        יש למלא את כל השדות ולאשר את כל ההצהרות הנדרשות לפני
                        המעבר לתשלום
                      </p>
                    </div>}

                  <Button type="submit" disabled={!isFormValid()} className={`w-full py-4 md:py-5 text-base md:text-lg font-bold rounded-xl transition-all ${isFormValid() ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-447">
                    {isFormValid() ? <span className="flex items-center justify-center gap-2" data-id="element-448">
                        <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6" data-id="element-449" />
                        המשך לתשלום
                      </span> : 'נא למלא את כל השדות'}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}